import { memo, useCallback, useState } from 'react';

import { useTranslateFn } from '@shared/Translate';

/**
 * A button component that allows focusing on tabs when clicked or activated via keyboard.
 * leverage on the `button.showOnFocus:focus-visible` CSS class to show the button only when focused.
 * @param {Object} props - Component props
 * @param {string} [props.tabId] - Optional identifier of the tab to focus (for livescores calendar)
 * @param {Function} props.onFocus - Callback function to trigger when the button is focused or activated
 * @returns {React.ReactElement} A button element with accessibility attributes
 */
interface ShowOnFocusButtonProps {
  tabId?: string;
  onFocus: (tabId?: string) => void;
}

const ShowOnFocusButton = ({ tabId, onFocus }: ShowOnFocusButtonProps) => {
  const t = useTranslateFn();
  const [isButtonFocused, setIsButtonFocused] = useState(false);

  const focusTab = useCallback(() => {
    onFocus?.(tabId);
  }, [onFocus, tabId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        onFocus?.(tabId);
      }
    },
    [onFocus, tabId],
  );

  const handleButtonFocus = useCallback(() => {
    setIsButtonFocused(true);
  }, []);

  const handleButtonBlur = useCallback(() => {
    setIsButtonFocused(false);
  }, []);

  return (
    <button
      type='button'
      aria-label={t('goto_tabs')}
      className='sr-only sr-only-focusable showOnFocus'
      aria-hidden={!isButtonFocused}
      onClick={focusTab}
      onKeyDown={handleKeyDown}
      onFocus={handleButtonFocus}
      onBlur={handleButtonBlur}
    >
      {t('goto_tabs')}
    </button>
  );
};

export default memo(ShowOnFocusButton);
